import React, {useRef, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

export const StepBettingRugby = ({
                                     userBet,
                                     fixture,
                                     handleBetChange,
                                     calculations,
                                     onValidate,
                                     quizPoints = 0
                                 }) => {

    const {t} = useTranslation();
    const validateBtnRef = useRef(null);
    const [showMargin, setShowMargin] = useState(false);
    const isFirstRender = useRef(true);

    const homeTeam = fixture.homeTeam;
    const awayTeam = fixture.awayTeam;

    const getOdd = (type) => fixture.odds?.find(o => o.marketType === type)?.value || '-';
    const oddHome = getOdd('HOME_WINNER');
    const oddDraw = getOdd('DRAW');
    const oddAway = getOdd('AWAY_WINNER');

    useEffect(() => {
        setShowMargin(userBet.selection && userBet.selection !== 'DRAW');
    }, [userBet.selection]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (userBet.selection) {
            const scrollDelay = userBet.selection === 'DRAW' ? 100 : 550;
            const timer = setTimeout(() => {
                if (validateBtnRef.current) {
                    validateBtnRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest'
                    });
                }
            }, scrollDelay);
            return () => clearTimeout(timer);
        }
    }, [userBet.selection]);

    const handleSelection = (sel) => {
        handleBetChange('selection', sel);
        if (sel === 'DRAW') {
            handleBetChange('margin', 0);
        } else {
            if (userBet.margin === 0 || userBet.margin === undefined) {
                handleBetChange('margin', 1);
            }
        }
    };

    const adjustMargin = (amount) => {
        let newVal = (userBet.margin || 0) + amount;
        if (newVal < 1) newVal = 1;
        if (newVal > 60) newVal = 60;
        handleBetChange('margin', newVal);
    };

    return (
        <div className="mt-6 pb-24">

            <style>{`
                /* SLIDER MOBILE-FRIENDLY (GROS DOIGTS) */
                input[type=range]::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    height: 44px;
                    width: 44px;
                    border-radius: 50%;
                    background: white;
                    border: 4px solid #10B981;
                    box-shadow: 0 4px 10px rgba(0,0,0,0.2);
                    cursor: pointer;
                    margin-top: -16px;
                    position: relative;
                    z-index: 10;
                }
                
                input[type=range]::-webkit-slider-runnable-track {
                    width: 100%;
                    height: 12px;
                    cursor: pointer;
                    background: #E5E7EB;
                    border-radius: 99px;
                }

                input[type=range]::-moz-range-thumb {
                    height: 44px;
                    width: 44px;
                    border: 4px solid #10B981;
                    border-radius: 50%;
                    background: white;
                    cursor: pointer;
                }
            `}</style>

            <section className="mb-6 animate-fade-in">
                <SectionTitle title={t('betting.rugby.step1_title')}/>
                <div className="grid grid-cols-3 gap-3">
                    <SelectableCard
                        team={homeTeam}
                        label={homeTeam.name}
                        odd={oddHome}
                        isSelected={userBet.selection === 'HOME'}
                        onClick={() => handleSelection('HOME')}
                    />
                    <SelectableCard
                        label={t('betting.rugby.draw')}
                        odd={oddDraw}
                        isDraw={true}
                        isSelected={userBet.selection === 'DRAW'}
                        onClick={() => handleSelection('DRAW')}
                    />
                    <SelectableCard
                        team={awayTeam}
                        label={awayTeam.name}
                        odd={oddAway}
                        isSelected={userBet.selection === 'AWAY'}
                        onClick={() => handleSelection('AWAY')}
                    />
                </div>
            </section>

            <div
                className={`transition-all duration-500 ease-in-out overflow-hidden ${showMargin ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <section className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 mb-6 relative">
                    <SectionTitle title={t('betting.rugby.step2_title')} centered/>
                    <div className="flex flex-col items-center">

                        <div className="flex items-center gap-6 mb-8">
                            <button
                                onClick={() => adjustMargin(-1)}
                                className="w-12 h-12 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-bold text-2xl active:scale-90 transition hover:bg-slate-200 touch-manipulation"
                            >
                                －
                            </button>

                            <div className="text-6xl font-black text-slate-800 tracking-tighter w-32 text-center">
                                +{userBet.margin}
                            </div>

                            <button
                                onClick={() => adjustMargin(1)}
                                className="w-12 h-12 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-bold text-2xl active:scale-90 transition hover:bg-slate-200 touch-manipulation"
                            >
                                ＋
                            </button>
                        </div>

                        <div className="w-full px-4 mb-8 relative h-14 flex items-center">
                            <input
                                type="range"
                                min="1"
                                max="30"
                                step="1"
                                value={userBet.margin || 1}
                                onChange={(e) => handleBetChange('margin', parseInt(e.target.value))}
                                className="w-full bg-transparent appearance-none focus:outline-none touch-pan-y"
                            />
                        </div>

                        <div className="grid grid-cols-4 gap-2 w-full">
                            {[5, 10, 15, 20].map(val => (
                                <button
                                    key={val}
                                    onClick={() => handleBetChange('margin', val)}
                                    className={`
                                        py-3 rounded-xl text-sm font-bold transition-all active:scale-95 touch-manipulation
                                        ${userBet.margin === val
                                        ? 'bg-slate-800 text-white shadow-md'
                                        : 'bg-slate-50 text-slate-500 border border-slate-200'}
                                    `}
                                >
                                    +{val}
                                </button>
                            ))}
                        </div>
                    </div>
                </section>
            </div>

            <div
                className={`transition-all duration-500 delay-100 ${userBet.selection ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                <div
                    className="bg-slate-900 rounded-2xl p-5 shadow-xl shadow-slate-900/10 mb-4 text-white relative overflow-hidden">
                    <div
                        className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-3xl translate-x-10 -translate-y-10"></div>

                    <div className="flex justify-between items-center relative z-10">
                        <div>
                            <div
                                className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{t('betting.rugby.max_gain')}</div>
                            <div
                                className="text-4xl font-black text-action-green tracking-tighter leading-none">{calculations.total}</div>
                            <div
                                className="text-[10px] text-slate-500 font-medium mt-1">{t('betting.rugby.exact_margin')}</div>
                        </div>
                        {userBet.selection !== 'DRAW' && (
                            <div className="text-right">
                                <div
                                    className="text-[10px] font-bold text-amber-500 uppercase tracking-widest mb-1 flex items-center justify-end gap-1">
                                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                              d="M13 10V3L4 14h7v7l9-11h-7z"/>
                                    </svg>
                                    {t('betting.rugby.risk')}
                                </div>
                                <div className="text-xl font-bold text-white tracking-tight">
                                    -{calculations.costPerError} <span
                                    className="text-xs font-normal text-slate-400">{t('betting.rugby.pts')}</span>
                                </div>
                                <div className="text-[10px] text-slate-500 font-medium mt-1">
                                    {t('betting.rugby.per_error_point')}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {quizPoints > 0 && (
                    <div className="mt-3 flex items-center justify-between bg-action-green/5 border border-action-green/15 rounded-lg p-2.5">
                        <span className="text-xs font-bold text-action-green uppercase flex items-center gap-1">
                            <span>⚡</span> {t('betting.quiz_bonus', 'Bonus quiz acquis')}
                        </span>
                        <span className="text-sm font-black text-action-green">+{quizPoints.toLocaleString()} {t('common.points')}</span>
                    </div>
                )}

                <button
                    ref={validateBtnRef}
                    disabled={!userBet.selection}
                    onClick={onValidate}
                    className={`w-full py-4 font-bold rounded-xl text-lg shadow-lg transition-all 
                    ${!userBet.selection
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-action-green text-white hover:brightness-110 active:scale-98'}`}
                >
                    {t('betting.validate_btn')}
                </button>
            </div>
        </div>
    );
};

const SectionTitle = ({title, centered}) => (
    <h3 className={`text-[10px] font-bold text-gray-400 uppercase mb-3 tracking-[0.2em] ${centered ? 'text-center' : 'ml-1'}`}>
        {title}
    </h3>
);

const SelectableCard = ({label, team, odd, isSelected, onClick, isDraw}) => {
    if (isSelected) {
        return (
            <button onClick={onClick}
                    className="relative flex flex-col items-center justify-between p-3 rounded-2xl border-2 transition-all duration-300 h-32 w-full touch-manipulation bg-white border-action-green ring-2 ring-action-green/20 shadow-xl -translate-y-1 z-10">
                <Content isDraw={isDraw} team={team} label={label} odd={odd} state="selected"/>
                <div className="absolute -top-2 -right-2 bg-action-green text-white rounded-full p-1 shadow-md">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M5 13l4 4L19 7"/>
                    </svg>
                </div>
            </button>
        );
    }
    return (
        <button onClick={onClick}
                className="relative flex flex-col items-center justify-between p-3 rounded-2xl border-2 transition-all duration-300 h-32 w-full touch-manipulation bg-white border-slate-100 shadow-md hover:border-slate-300 hover:-translate-y-0.5">
            <Content isDraw={isDraw} team={team} label={label} odd={odd} state="neutral"/>
        </button>
    );
};

const Content = ({isDraw, team, label, odd, state}) => {
    const isFlag = team?.code || team?.logoUrl?.startsWith('https://flagcdn.com/');
    let imgSrc = null;
    if (team) {
        imgSrc = team.code
            ? `https://flagcdn.com/w160/${team.code.toLowerCase()}.png`
            : team.logoUrl;
    }

    return (
        <>
            <div className="flex-1 flex items-center justify-center w-full">
                {isDraw ? (
                    <span
                        className={`text-2xl font-black ${state === 'selected' ? 'text-slate-800' : 'text-slate-300'}`}>🤝</span>
                ) : (
                    imgSrc ? (
                        <div className={`relative ${
                            isFlag
                                ? 'w-[56px] h-[40px] overflow-hidden rounded-md flag-box shadow-sm'
                                : 'w-12 h-12'
                        } ${state === 'selected' ? 'scale-110' : ''} transition-transform`}>
                            <img
                                src={imgSrc}
                                alt={label}
                                className={isFlag
                                    ? "w-full h-full object-fill"
                                    : "w-full h-full object-contain drop-shadow-sm"
                                }
                                onError={(e) => e.target.src = 'https://via.placeholder.com/50?text=?'}
                            />
                        </div>
                    ) : (
                        <div
                            className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-400">?</div>
                    )
                )}
            </div>

            <span className={`text-[10px] font-black px-2 py-1 rounded-lg transition-colors 
            ${state === 'selected' ? 'bg-action-green text-white' : 'bg-slate-100 text-slate-500'}`}>
            {odd}
        </span>
        </>
    );
};